import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HelpCircle, MessageCircle } from "lucide-react";

const FAQ = () => {
  const faqs = [
    {
      id: "faq-1",
      question: "¿Puedo recibir asesoría sin costo?",
      answer: "¡Absolutamente! Ofrecemos asesoría gratuita y personalizada para todos nuestros clientes. Nuestros expertos evaluarán su espacio, escucharán sus necesidades y le proporcionarán recomendaciones profesionales sin ningún compromiso. También incluimos cotizaciones detalladas sin costo."
    },
    {
      id: "faq-2",
      question: "¿Cómo elijo entre piso de madera sólida y de ingeniería?",
      answer: "La elección depende de varios factores: La madera sólida es ideal para espacios con tráfico moderado y ofrece mayor durabilidad a largo plazo, permitiendo múltiples lijados. La madera de ingeniería es más estable dimensionalmente, mejor para áreas con cambios de humedad, y generalmente más económica. Nuestros asesores evaluarán su espacio específico para recomendarle la mejor opción."
    },
    {
      id: "faq-3",
      question: "¿Los productos tienen garantía?",
      answer: "Sí, todos nuestros productos cuentan con garantías sólidas que varían según el tipo: Pisos de madera sólida (10 años), pisos de ingeniería (8 años), pisos vinílicos SPC (15 años), pasto sintético (8 años), y paneles decorativos (5 años). Además, ofrecemos garantía de instalación de 2 años en todos nuestros trabajos."
    },
    {
      id: "faq-4",
      question: "¿Hacen entregas a domicilio?",
      answer: "Sí, realizamos entregas en toda el área metropolitana de San Salvador y municipios cercanos. Para proyectos grandes, coordinamos la logística de entrega por etapas según el cronograma de instalación. También tenemos convenios con empresas de transporte para entregas a nivel nacional. Los costos de envío se calculan según la distancia y volumen del pedido."
    },
    {
      id: "faq-5",
      question: "¿Tienen muestras físicas?",
      answer: "Sí, ofrecemos servicio de muestras a domicilio para proyectos importantes, donde llevamos las opciones directamente a su espacio para que pueda visualizar mejor cómo se verán con su iluminación y decoración existente."
    }
  ];

  const additionalQuestions = [
    "¿Cuánto tiempo toma la instalación?",
    "¿Incluyen el retiro del piso existente?",
    "¿Trabajan con arquitectos y diseñadores?",
    "¿Ofrecen financiamiento?",
    "¿Qué incluye el mantenimiento?"
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-primary mb-4">
            Preguntas Frecuentes
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Encuentre respuestas a las consultas más comunes sobre nuestros productos y servicios.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* FAQ Accordion */}
          <div className="lg:col-span-2">
            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="font-playfair text-2xl text-primary flex items-center">
                  <HelpCircle className="mr-3 text-wood-warm" size={24} />
                  Preguntas y Respuestas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="space-y-4">
                  {faqs.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id} className="border border-border rounded-lg px-4">
                      <AccordionTrigger className="font-medium text-left hover:text-primary">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Contact CTA */}
            <Card className="shadow-soft bg-gradient-warm text-primary-foreground">
              <CardContent className="p-6 text-center">
                <MessageCircle className="mx-auto mb-4 text-primary-foreground" size={32} />
                <h3 className="font-playfair text-xl font-semibold mb-3">
                  ¿No encontró su respuesta?
                </h3>
                <p className="mb-4 text-primary-foreground/90">
                  Nuestros expertos están listos para resolver todas sus dudas personalmente.
                </p>
                <Button 
                  variant="outline" 
                  className="bg-white/20 backdrop-blur-sm border-white/30 text-primary-foreground hover:bg-white/30"
                  onClick={() => {
                    const element = document.getElementById('contacto');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Contactar Experto
                </Button>
              </CardContent>
            </Card>

            {/* Additional Questions */}
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="font-playfair text-xl text-primary">
                  Otras Preguntas Comunes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {additionalQuestions.map((question, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-wood-warm rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      <span className="text-muted-foreground text-sm leading-relaxed">{question}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground text-center">
                    ¿Tiene alguna de estas preguntas? Contáctenos para obtener información detallada.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="font-playfair text-xl text-primary">
                  Consejos Rápidos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm">
                  <h4 className="font-medium text-primary mb-2">Antes de su cita:</h4>
                  <ul className="text-muted-foreground space-y-1 text-xs">
                    <li>• Tenga las medidas aproximadas</li>
                    <li>• Considere su presupuesto</li>
                    <li>• Piense en el uso del espacio</li>
                  </ul>
                </div>
                <div className="text-sm">
                  <h4 className="font-medium text-primary mb-2">Para mejores resultados:</h4>
                  <ul className="text-muted-foreground space-y-1 text-xs">
                    <li>• Solicite múltiples muestras</li>
                    <li>• Consulte sobre mantenimiento</li>
                    <li>• Pregunte por garantías</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;